import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, Chip } from '@heroui/react';

import type { SSEEvent } from '~/types';

import { ProjectActivityEvent } from './Event';
import { formatEventMessage, useProjectName } from './utils';

interface ProjectActivityItemProps {
  projectId: string;
  events: SSEEvent[];
  onClear: () => void;
  isActiveJob: boolean;
}

export function ProjectActivityItem(props: ProjectActivityItemProps): JSX.Element {
  const { events, isActiveJob, onClear, projectId } = props;
  const projectName = useProjectName(projectId);
  const [isExpanded, setIsExpanded] = useState(isActiveJob);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = (): void => {
    setIsExpanded(!isExpanded);
  };

  // Auto-expand if this becomes an active job
  useEffect(() => {
    if (isActiveJob && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isActiveJob, isExpanded]);

  // Auto-scroll to newest event when events change and this is the active job
  useEffect(() => {
    if (isActiveJob && isExpanded && eventsEndRef.current) {
      eventsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [events, isActiveJob, isExpanded]);

  return (
    <div className="border border-default rounded-lg">
      {/* Project Header */}
      <div className="flex items-center justify-between p-4 border-b border-default bg-surface-elevated">
        <p className="flex gap-2 items-center">
          <Button
            aria-label={isExpanded ? 'Collapse project' : 'Expand project'}
            isIconOnly
            onPress={toggleExpanded}
            size="sm"
            variant="light"
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4 text-muted" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-muted" />
            )}
          </Button>
          <span className="font-medium truncate">{projectName}</span>
          <Chip as="span" className="px-2 bg-surface " size="sm">
            {events.length}
          </Chip>
        </p>
        <Button
          aria-label="Clear events for this project"
          onPress={onClear}
          isIconOnly
          size="sm"
          variant="light"
          className=" hover:text-danger-600 dark:hover:text-danger-400"
        >
          <TrashIcon className="w-4 h-4" />
        </Button>
      </div>

      {/* Event List */}
      {isExpanded && (
        <div className="p-4 space-y-2 max-h-full overflow-y-auto">
          {events.map((event, index) => {
            // Filter out events that have no displayable content
            const message = formatEventMessage(event);
            if (message === null) return null;
            return <ProjectActivityEvent key={event.id || index} {...event} />;
          })}
          <div ref={eventsEndRef} />
        </div>
      )}
    </div>
  );
}
